import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerContent,
  VStack,
  Box,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Flex,
  CircularProgress,
  CircularProgressLabel,
  Image,
  DrawerFooter,
  HStack
} from '@chakra-ui/react';
import { factCheck } from '../../../../apis/article-viewer';
import FactCheckIcon from '../../../../assets/fact-check-green-icon.svg';
import WarningErrorIcon from '../../../../assets/warning-error-icon.svg';
import InformIcon from '../../../../assets/inform-green-icon.svg';
import Badge from '../../../Badge';
import ProgressBar from '../../../ProgressBar';
import { TooltipComponent } from '../../../Tooltip';
import Button from '../../../Button';
import CloseModalIcon from '../../../../assets/close-modal-icon.svg';

type FactCheck = {
  originalClaim: string;
  claims: Array<{
    text: string;
    type: string;
    accuracy: string;
    explanation: string;
    confidence_level: number;
    verification_source: {
      url: string;
      type: string;
      description: string;
    };
  }>;
  summary: {
    key_findings: string;
    overall_reliability: number;
  };
  metadata: {
    version: string;
    timestamp: string;
  };
};

const getAccuracyBgColor = (assessment: string) => {
  switch (assessment) {
    case 'accurate':
      return '#4CAF50';
    case 'inaccurate':
      return '#DE1414';
    case 'partially_accurate':
      return '#FFD166';
    default:
      return '#D1C8C4';
  }
};

const getAccuracyTextColor = (assessment: string) => {
  switch (assessment) {
    case 'accurate':
      return 'white';
    case 'inaccurate':
      return 'white';
    case 'partially_accurate':
      return 'black';
    default:
      return 'black';
  }
};

type FactCheckDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedText: string;
  articleId: string;
  initialFactChecks: FactCheck[];
};

const FactCheckDrawer = ({
  isOpen,
  onClose,
  selectedText,
  articleId,
  initialFactChecks = []
}: FactCheckDrawerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [factChecks, setFactChecks] = useState<FactCheck[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    setFactChecks([...initialFactChecks].reverse());
  }, [initialFactChecks]);

  useEffect(() => {
    if (!selectedText || !isOpen) return;

    const fetchFactCheck = async () => {
      setIsLoading(true);

      try {
        const response = await factCheck({
          provider: 'OpenAI',
          prompt: selectedText,
          articleId
        });
        setFactChecks((prevChecks) => [response, ...prevChecks]);
      } catch (error) {
        console.error('Error fetching fact check:', error);
        const isLimited = error?.response?.data?.message?.search('limit') > 0;
        setShowErrorModal(isLimited);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFactCheck();
  }, [selectedText, isOpen, articleId]);

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      blockScrollOnMount={false}
      trapFocus={false}
      size={'lg'}
    >
      <DrawerContent bg="#edf7ee" pb="env(safe-area-inset-bottom)" w={'100%'}>
        {showErrorModal && (
          <div className="w-[100%] h-[100%] bg-[white] bg-opacity-95 absolute z-10 top-0 flex justify-center items-center">
            <div className="text-center">
              <img src={WarningErrorIcon} alt="Warning Error" className="m-auto" />
              <Text className="text-[16px] text-error-red font-[700] my-[24px]">
                You’ve reached the limit of 4 fact checks for this Article.
              </Text>
              <div className="flex items-center justify-center">
                <Button variant={'outline'} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant={'primary'}
                  className="ml-2"
                  onClick={() => {
                    setShowErrorModal(false);
                  }}
                >
                  Access your previous checks
                </Button>
              </div>
            </div>
          </div>
        )}

        <DrawerHeader p={4} m={0} maxW={'unset'}>
          <HStack justify="space-between" pb={2} borderBottom={'1px solid #D1C8C4'} w={'100%'}>
            <Flex align="center" gap={2}>
              <Image src={FactCheckIcon} alt="Twygsbot" h="24px" />
              <Text
                fontWeight="bold"
                fontSize="20px"
                className="text-growth-green-500"
                whiteSpace={'nowrap'}
              >
                Twygsbot Fact Check
              </Text>
            </Flex>
            <img
              src={CloseModalIcon}
              onClick={onClose}
              className="h-9 w-9 cursor-pointer"
              color="growth-green"
              alt=""
            />
          </HStack>
        </DrawerHeader>

        <DrawerBody p={4}>
          {isLoading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="lg" color="green.500" />
            </Flex>
          ) : !factChecks?.length ? (
            <Text color="gray.500" textAlign="center">
              No fact checks yet
            </Text>
          ) : (
            <Tabs>
              <TabList overflowX={'auto'} overflowY={'hidden'}>
                {factChecks?.map((check, index) => (
                  <Tab
                    key={check?.metadata?.timestamp}
                    sx={{
                      _selected: {
                        borderBottom: '4px solid #4CAF50'
                      }
                    }}
                    className="font-[800] whitespace-nowrap"
                  >
                    Fact Check {index + 1}
                  </Tab>
                ))}
              </TabList>

              <TabPanels>
                {factChecks?.map((check) => (
                  <TabPanel key={check?.metadata?.timestamp} p={0}>
                    <Box borderRadius="lg">
                      <VStack spacing={6} align="stretch" mt={'32px'}>
                        <Box bg="white" p={6}>
                          <Text fontWeight="800" fontSize="22" className="text-growth-green-500">
                            Original Text
                          </Text>
                          <Box className="block md:flex md:justify-between mt-4">
                            <Box
                              px={3}
                              className="border-l-2 border-growth-green-400 text-growth-green-900"
                            >
                              {check?.originalClaim}
                            </Box>
                            <Box className="grid grid-rows-[fit-content, fit-content]">
                              <Box className="flex justify-center items-center">
                                <CircularProgress
                                  value={check?.summary.overall_reliability * 10}
                                  color={
                                    check?.summary.overall_reliability > 7
                                      ? 'green.400'
                                      : check?.summary.overall_reliability > 4
                                        ? 'yellow.400'
                                        : 'red.400'
                                  }
                                  size={20}
                                  thickness={17}
                                >
                                  <CircularProgressLabel fontSize="sm" fontWeight={800}>
                                    {check?.summary.overall_reliability}/10
                                  </CircularProgressLabel>
                                </CircularProgress>
                              </Box>
                              <Box className="flex pt-2 m-auto">
                                <p className="text-02-dim-gray text-[10px] font-[800] mr-1 whitespace-nowrap">
                                  Reliability Score
                                </p>
                                <TooltipComponent
                                  Icon={InformIcon}
                                  description={'How accurate the original text is.'}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        <Box>
                          <Text fontWeight="medium" fontSize="24" mb={1}>
                            Claims Analysis
                          </Text>
                          <Text fontWeight="medium" fontSize="sm" className="text-growth-green-900">
                            Claims found in the original text you have highlighted
                          </Text>

                          <VStack spacing={4} align="stretch" mt={8}>
                            {check?.claims?.map((claim, index) => (
                              <Box
                                key={index}
                                bg="white"
                                p={6}
                                border={'1px solid'}
                                borderColor={getAccuracyBgColor(claim.accuracy)}
                              >
                                <Box>
                                  <Text
                                    fontWeight="800"
                                    fontSize="18"
                                    className="text-growth-green-500"
                                  >
                                    Claim {index + 1}
                                  </Text>
                                  <Box
                                    className="block md:flex md:justify-between"
                                    marginTop={4}
                                    marginBottom={3}
                                  >
                                    <Box
                                      px={3}
                                      className="border-l-2 border-growth-green-400 text-growth-green-900"
                                    >
                                      {claim.text}
                                    </Box>
                                    <Badge
                                      color={getAccuracyTextColor(claim.accuracy)}
                                      bgColor={getAccuracyBgColor(claim.accuracy)}
                                      text={claim.accuracy}
                                      className="mt-5 md:my-auto"
                                    />
                                  </Box>
                                </Box>

                                <Box>
                                  <Text
                                    display={'inline'}
                                    fontWeight="800"
                                    fontSize="18"
                                    className="text-growth-green-500"
                                  >
                                    Response
                                  </Text>
                                  <Text fontSize="14" color="#204A22" mt={3}>
                                    {claim.explanation}
                                  </Text>
                                </Box>

                                <Box className="mt-3">
                                  <div className="grid grid-cols-2">
                                    <Box className="flex items-center">
                                      <p className="text-02-dim-gray text-[10px] font-[800] mr-1 whitespace-nowrap">
                                        Confidence level
                                      </p>
                                      <span className="mb-[2px]">
                                        <TooltipComponent
                                          Icon={InformIcon}
                                          description={
                                            'How confident AI is of the responses provided.'
                                          }
                                        />
                                      </span>
                                    </Box>
                                    <Text className="text-right text-02-dim-gray text-[10px] font-[800]">
                                      {claim.confidence_level}%
                                    </Text>
                                  </div>
                                  <ProgressBar
                                    bgColor={
                                      'linear-gradient(90deg, #C8E6C9 1.02%, #4CAF50 55.95%, #204A22 91.59%)'
                                    }
                                    value={claim.confidence_level}
                                  />
                                </Box>

                                <Box
                                  mt="5"
                                  className="bg-growth-green-50"
                                  border={'1px solid'}
                                  borderColor={'#C8E6C9'}
                                  p={3}
                                >
                                  <Text
                                    fontSize="12"
                                    fontWeight="medium"
                                    className={'text-growth-green-900'}
                                    mb={1}
                                  >
                                    How to verify:
                                  </Text>
                                  <Text fontSize="12" className="text-01-jet-black mb-2">
                                    {claim.verification_source.description}
                                  </Text>
                                  <Text fontSize="12" className="text-growth-green-500">
                                    <a
                                      href={claim.verification_source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hover:underline flex items-center"
                                    >
                                      Visit source
                                      <svg
                                        className="w-3 h-3 ml-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                        />
                                      </svg>
                                    </a>
                                  </Text>
                                </Box>
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          Fact checked on {new Date(check?.metadata?.timestamp).toLocaleString()}
                        </Text>
                      </VStack>
                    </Box>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          )}
        </DrawerBody>

        <DrawerFooter flexDirection={'row-reverse'}>
          <Text fontSize="xs" color="gray.500" mt={4} textAlign="center">
            AI responses may be wrong or biased. Always verify sources and cross-check information
            independently.
          </Text>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default FactCheckDrawer;
